const API_BASE = '/api';

// Auth token management
function getToken() {
  return localStorage.getItem('scout_token') || '';
}

export function setToken(token) {
  localStorage.setItem('scout_token', token);
}

export function clearToken() {
  localStorage.removeItem('scout_token');
}

export function hasToken() {
  return !!getToken();
}

async function request(url, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });
  
  if (res.status === 401) {
    clearToken();
    window.dispatchEvent(new Event('scout-unauthorized'));
    throw new Error('Unauthorized — please log in again');
  }

  if (!res.ok) {
    let errorMsg = `Request failed: ${res.status}`;
    try {
      const data = await res.json();
      errorMsg = data.error || errorMsg;
    } catch { /* ignore parse failure */ }
    throw new Error(errorMsg);
  }
  
  return res.json();
}

// Returns { success, data: { ticket, comments, satisfaction, users, cara, assignee, requester } }
export async function getTicket(id) {
  return request(`/ticket/${id}`);
}

// Returns { success, ticketId, data, analysis }
export async function analyzeTicket(id) {
  return request(`/ticket/${id}/analyze`, { method: 'POST' });
}

// Returns { success, results: [...] }
export async function batchAnalyze(ticketIds) {
  return request('/tickets/analyze', {
    method: 'POST',
    body: JSON.stringify({ ticketIds }),
  });
}

// Returns { success, searchPlan, results, resultCount }
export async function investigate(text) {
  return request('/investigate', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

// Returns { success, tickets: [...], count }
export async function getCSATRatings(startDate, endDate, assignee) {
  return request('/csat-review', {
    method: 'POST',
    body: JSON.stringify({ startDate, endDate, assignee }),
  });
}

// Returns { success, analyses: [...], summary }
export async function analyzeCSATBatch(ticketIds) {
  return request('/csat-review/analyze', {
    method: 'POST',
    body: JSON.stringify({ ticketIds }),
  });
}

// Returns { success, agents: [{ id, name, email, role }] }
export async function getAgents() {
  return request('/agents');
}

// Returns { success, tickets: [...], count, nextPage }
export async function searchTickets(query, page = 1) {
  return request('/search', {
    method: 'POST',
    body: JSON.stringify({ query, page }),
  });
}

// --- CSAT V2 (Ben's Taxonomy) ---

// Pull bad CSAT tickets for date range
export async function csatPull(startDate, endDate, assignee) {
  return request('/csat/pull', {
    method: 'POST',
    body: JSON.stringify({ startDate, endDate, assignee }),
  });
}

// Analyze batch using Ben's taxonomy
export async function csatAnalyzeBatch(ticketIds) {
  return request('/csat/analyze-batch', {
    method: 'POST',
    body: JSON.stringify({ ticketIds }),
  });
}

// Export as XLSX
export async function csatExport(results, dateRange) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/csat/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ results, dateRange }),
  });
  if (!res.ok) throw new Error('Export failed');
  return res.blob();
}

// Analyze pasted email
export async function csatAnalyzeEmail(content) {
  return request('/csat/analyze-email', {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

// Test auth
export async function testAuth(token) {
  const res = await fetch(`${API_BASE}/health`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return res.ok;
}
